import React from 'react';
import * as Mapbox from "react-map-gl/mapbox";
import {
  FillLayerSpecification,
  FilterSpecification,
  LayerSpecification,
  LineLayerSpecification,
  SymbolLayerSpecification
} from "mapbox-gl";
import FeatureFlag from "../../../types/FeatureFlag";
import {useAuth} from "../../../context/AuthContext";
import {MapLayer} from "../../../types/MapLayers";

// Types ----

type ZoneLayerProps = {
  source?: string
  enabledLayers: MapLayer[]
}

// Component ----

function ZoneLayer({source = '', enabledLayers}: ZoneLayerProps) {
  const {can, canUse} = useAuth();

  const buildingsEnabled = can('buildings:read') && canUse(FeatureFlag.IndoorMapping);

  // Filters
  const filter: FilterSpecification = [
    'all',
    ['==', ['get', 'type'], 'zone'],
    // TODO: Ternary conditional filter can be removed when buildings exist in the API
    buildingsEnabled ? ['==', ['get', 'id', ['get', 'space']], null] : ['==', ['get', 'type'], 'zone'],
  ];

  // Layout Properties
  const layout: LayerSpecification['layout'] = { visibility: enabledLayers.includes(MapLayer.ZONE) ? 'visible' : 'none' };

  return (
    <>
      <Mapbox.Layer
        type="fill"
        id="ngage-zone"
        source={source}
        filter={filter}
        paint={zonePaint}
        layout={layout}
      />

      <Mapbox.Layer
        type="line"
        id="ngage-zone-outline"
        source={source}
        filter={filter}
        paint={zoneOutlinePaint}
        layout={layout}
      />

      <Mapbox.Layer
        type="symbol"
        id="ngage-zone-name"
        source={source}
        filter={filter}
        layout={{...zoneNameLayout, ...layout}}
        paint={zoneNamePaint}
      />
    </>
  );
}

// Styles ----

const zonePaint: FillLayerSpecification['paint'] = {
  "fill-color": "#9a9a9a",
  "fill-opacity": 0.3,
}

const zoneOutlinePaint: LineLayerSpecification['paint'] = {
  "line-color": "#949494",
  "line-width": 2,
  "line-dasharray": [1, 1],
}

const zoneNameLayout: SymbolLayerSpecification['layout'] = {
  "text-field": ["get", "name"],
  "text-anchor": "center",
}

const zoneNamePaint: SymbolLayerSpecification['paint'] = {
  "text-halo-width": 2,
  "text-halo-color": "#FFFFFF",
}

export default ZoneLayer;